import React, { useEffect, useState, createElement } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Copy, Download, TrendingUp, PieChart } from 'lucide-react';
interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}
export function MortgageCalculator() {
  const [loanAmount, setLoanAmount] = useState('300000');
  const [interestRate, setInterestRate] = useState('6.5');
  const [loanTerm, setLoanTerm] = useState('30');
  const [region, setRegion] = useState('US');
  const [result, setResult] = useState<{
    monthly: number;
    total: number;
    totalInterest: number;
    schedule: AmortizationRow[];
  } | null>(null);
  const calculate = () => {
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(interestRate) / 100 / 12;
    const payments = parseFloat(loanTerm) * 12;
    if (isNaN(principal) || isNaN(rate) || isNaN(payments) || principal <= 0) return;
    const monthly = principal * rate * Math.pow(1 + rate, payments) / (Math.pow(1 + rate, payments) - 1);
    const total = monthly * payments;
    const totalInterest = total - principal;
    // Generate schedule
    let balance = principal;
    const schedule: AmortizationRow[] = [];
    for (let i = 1; i <= payments; i++) {
      const interestPayment = balance * rate;
      const principalPayment = monthly - interestPayment;
      balance = Math.max(0, balance - principalPayment);
      schedule.push({
        month: i,
        payment: monthly,
        principal: principalPayment,
        interest: interestPayment,
        balance: balance
      });
    }
    setResult({
      monthly: Math.round(monthly * 100) / 100,
      total: Math.round(total * 100) / 100,
      totalInterest: Math.round(totalInterest * 100) / 100,
      schedule
    });
  };
  useEffect(() => {
    calculate();
  }, [loanAmount, interestRate, loanTerm]);
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  const exportToCSV = () => {
    if (!result) return;
    const headers = ['Month,Payment,Principal,Interest,Balance'];
    const rows = result.schedule.map(row => `${row.month},${row.payment.toFixed(2)},${row.principal.toFixed(2)},${row.interest.toFixed(2)},${row.balance.toFixed(2)}`);
    const csvContent = 'data:text/csv;charset=utf-8,' + headers.concat(rows).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `mortgage_schedule.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const currencySymbol = region === 'US' ? '$' : region === 'UK' ? '£' : 'A$';
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: region === 'US' ? 'USD' : region === 'UK' ? 'GBP' : 'AUD',
      maximumFractionDigits: 2
    }).format(amount);
  };
  return <div className="container mx-auto px-4 py-8 max-w-6xl" data-id="element-281">
      <div className="mb-8" data-id="element-282">
        <h1 className="text-4xl font-bold mb-2 text-gradient" data-id="element-283">
          Mortgage Calculator
        </h1>
        <p className="text-muted-foreground" data-id="element-284">
          Calculate your monthly mortgage payments with detailed amortization
          schedule
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-285">
        <Card className="lg:col-span-1 h-fit card-hover border-primary/20" data-id="element-286">
          <CardHeader data-id="element-287">
            <CardTitle data-id="element-288">Loan Details</CardTitle>
            <CardDescription data-id="element-289">Enter your mortgage information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4" data-id="element-290">
            <Select label="Region" value={region} onChange={e => setRegion(e.target.value)} options={[{
            value: 'US',
            label: 'United States'
          }, {
            value: 'UK',
            label: 'United Kingdom'
          }, {
            value: 'AU',
            label: 'Australia'
          }]} data-id="element-291" />
            <Input type="number" label="Loan Amount" value={loanAmount} onChange={e => setLoanAmount(e.target.value)} placeholder="300000" data-id="element-292" />
            <Input type="number" label="Interest Rate (%)" value={interestRate} onChange={e => setInterestRate(e.target.value)} placeholder="6.5" step="0.1" data-id="element-293" />
            <Input type="number" label="Loan Term (years)" value={loanTerm} onChange={e => setLoanTerm(e.target.value)} placeholder="30" data-id="element-294" />
            <Button onClick={calculate} className="w-full" data-id="element-295">
              Calculate Payment
            </Button>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-6" data-id="element-296">
          {result && <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-297">
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 shadow-lg" data-id="element-298">
                  <CardContent className="p-6 text-center" data-id="element-299">
                    <div className="flex items-center justify-center gap-2 mb-1" data-id="element-300">
                      <p className="text-sm font-medium text-muted-foreground" data-id="element-301">
                        Monthly Payment
                      </p>
                      <button onClick={() => copyToClipboard(result.monthly.toString())} className="opacity-50 hover:opacity-100" data-id="element-302">
                        <Copy className="w-3 h-3" data-id="element-303" />
                      </button>
                    </div>
                    <p className="text-3xl font-bold text-primary" data-id="element-304">
                      {formatCurrency(result.monthly)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/20" data-id="element-305">
                  <CardContent className="p-6 text-center" data-id="element-306">
                    <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-307">
                      Total Interest
                    </p>
                    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400" data-id="element-308">
                      {formatCurrency(result.totalInterest)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20" data-id="element-309">
                  <CardContent className="p-6 text-center" data-id="element-310">
                    <p className="text-sm font-medium text-muted-foreground mb-1" data-id="element-311">
                      Total Cost
                    </p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400" data-id="element-312">
                      {formatCurrency(result.total)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card data-id="element-313">
                <CardHeader data-id="element-314">
                  <CardTitle className="flex items-center gap-2" data-id="element-315">
                    <PieChart className="w-5 h-5" data-id="element-316" />
                    Cost Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-317">
                  <div className="flex items-center gap-4 mb-4" data-id="element-318">
                    <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden flex" data-id="element-319">
                      <div className="h-full bg-green-500" style={{
                    width: `${parseFloat(loanAmount) / result.total * 100}%`
                  }} data-id="element-320" />
                      <div className="h-full bg-orange-500" style={{
                    width: `${result.totalInterest / result.total * 100}%`
                  }} data-id="element-321" />
                    </div>
                  </div>
                  <div className="flex justify-center gap-8 text-sm" data-id="element-322">
                    <div className="flex items-center gap-2" data-id="element-323">
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-324" />
                      <span data-id="element-325">
                        Principal (
                        {(parseFloat(loanAmount) / result.total * 100).toFixed(1)}
                        %)
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-326">
                      <div className="w-3 h-3 rounded-full bg-orange-500" data-id="element-327" />
                      <span data-id="element-328">
                        Interest (
                        {(result.totalInterest / result.total * 100).toFixed(1)}
                        %)
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>}
        </div>
      </div>

      {result && <Card className="overflow-hidden" data-id="element-329">
          <CardHeader className="flex flex-row items-center justify-between" data-id="element-330">
            <div data-id="element-331">
              <CardTitle className="flex items-center gap-2" data-id="element-332">
                <TrendingUp className="w-5 h-5" data-id="element-333" />
                Amortization Schedule
              </CardTitle>
              <CardDescription data-id="element-334">Detailed payment breakdown</CardDescription>
            </div>
            <Button onClick={exportToCSV} variant="outline" size="sm" data-id="element-335">
              <Download className="w-4 h-4 mr-2" data-id="element-336" />
              Export CSV
            </Button>
          </CardHeader>
          <CardContent className="p-0" data-id="element-337">
            <div className="table-container max-h-[600px] overflow-y-auto" data-id="element-338">
              <table className="data-table" data-id="element-339">
                <thead data-id="element-340">
                  <tr data-id="element-341">
                    <th data-id="element-342">Month</th>
                    <th data-id="element-343">Payment</th>
                    <th data-id="element-344">Principal</th>
                    <th data-id="element-345">Interest</th>
                    <th data-id="element-346">Balance</th>
                  </tr>
                </thead>
                <tbody data-id="element-347">
                  {result.schedule.map(row => <tr key={row.month} data-id="element-348">
                      <td className="font-medium text-center" data-id="element-349">{row.month}</td>
                      <td data-id="element-350">{formatCurrency(row.payment)}</td>
                      <td className="text-green-600 dark:text-green-400 font-medium" data-id="element-351">
                        {formatCurrency(row.principal)}
                      </td>
                      <td className="text-orange-600 dark:text-orange-400 font-medium" data-id="element-352">
                        {formatCurrency(row.interest)}
                      </td>
                      <td data-id="element-353">{formatCurrency(row.balance)}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>}
    </div>;
}